import { lensPath, set, view, toPairs } from 'rambda';
import deepmerge from 'deepmerge';
import { Refinements } from '@packages/common';

import {
  FACET_FILTER_KEY_SEPERATOR,
  FILTER_VALUE_SEPARATOR,
  FACET_FILTER_PREFIX,
  FACET_FILTER_SEPARATOR,
  HERO_FILTER_KEY_SEPARATOR,
  HERO_FILTER_SEPARATOR,
} from './constants';

type IntermediateTransformation = {
  hero?: {
    [key: string]: string[];
  };
  normal?: {
    [key: string]: string[];
  };
};

const transformRefinementsToUrl = (refinements: Refinements): string => {
  const { facetFilters = {}, numericFilters = {}, page, priceSort, query, local } = refinements;
  const intermediateTransformationFacets: IntermediateTransformation = Object.values(
    facetFilters ?? {},
  ).reduce<IntermediateTransformation>(
    (acc, filter) =>
      filter.values.reduce<IntermediateTransformation>((current, value) => {
        const lens = lensPath(['normal', filter.seoName]);
        const currentItems: string[] = view(lens, current) || [];
        return set(lens, [...currentItems, value.seoName], current);
      }, acc),
    {},
  );

  const intermediateTransformationNumeric: IntermediateTransformation = Object.values(
    numericFilters ?? {},
  ).reduce<IntermediateTransformation>((acc, filter) => {
    const {
      seoName,
      value: { min, max },
    } = filter;
    const lens = lensPath(['normal', seoName]);
    return set(lens, [`${min}-${max}`], acc);
  }, {});

  const { hero, normal } = deepmerge(intermediateTransformationFacets, intermediateTransformationNumeric);

  const heroString = toPairs(hero || {}).reduce<string>((urlString, [filterName, values]) => {
    return [
      urlString,
      HERO_FILTER_SEPARATOR,
      filterName,
      HERO_FILTER_KEY_SEPARATOR,
      values.join(FILTER_VALUE_SEPARATOR),
    ].join('');
  }, '');

  const facetString = toPairs(normal || {}).reduce<string>((urlString, [filterName, values], index, arr) => {
    return [
      urlString,
      FACET_FILTER_PREFIX,
      filterName,
      FACET_FILTER_KEY_SEPERATOR,
      values.join(FILTER_VALUE_SEPARATOR),
      index >= arr.length - 1 ? '' : FACET_FILTER_SEPARATOR,
    ].join('');
  }, '');

  let otherFilters: Record<string, any> = {};
  if (page && page > 0) {
    otherFilters = {
      ...otherFilters,
      p: page,
    };
  }
  if (priceSort !== 'none') {
    otherFilters = {
      ...otherFilters,
      ps: priceSort,
    };
  }
  if (query !== '') {
    otherFilters = {
      ...otherFilters,
      query,
    };
  }
  if (local) {
    otherFilters = {
      ...otherFilters,
      local,
    };
  }

  const otherFiltersQueryString = toPairs(otherFilters)
    .reduce<string[]>((acc, [key, value]) => [...acc, `${key}=${value}`], [])
    .join('&');

  return `${heroString}${facetString !== '' ? '?' : ''}${facetString}${
    facetString !== '' && otherFiltersQueryString !== '' ? '&' : ''
  }${facetString === '' && otherFiltersQueryString !== '' ? '?' : ''}${otherFiltersQueryString}`;
};

export default transformRefinementsToUrl;
